import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Landmark, CreditCard, CheckCircle2, Upload, FileCheck2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { COURSE_DEPOSIT, CONTACT_NAME } from "./kursusConstants";

const darkField = "bg-kursus-bg2 border-kursus-border text-kursus-white placeholder:text-kursus-muted focus-visible:ring-kursus-gold focus-visible:border-kursus-gold rounded-md";

const EMPTY = { full_name: "", phone: "", ic_number: "", gender: "", location: "", prior_training: "", next_of_kin_name: "", next_of_kin_phone: "", current_occupation: "", payment_option: "deposit", payment_method: "bank_transfer", email: "", payment_receipt_file_id: "", notes: "" };

export function RegistrationForm() {
  const [form, setForm] = useState(EMPTY);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptFileName, setReceiptFileName] = useState("");
  const receiptInputRef = useRef(null);

  useEffect(() => {
    api.get("/public/payment-info").then((r) => setPaymentInfo(r.data)).catch(() => setPaymentInfo({ billplz_available: false }));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target?.value ?? e }));

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/course-leads/upload-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, payment_receipt_file_id: res.data.file_id }));
      setReceiptFileName(file.name);
      toast.success("Resit berjaya dimuat naik");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat naik resit");
    } finally {
      setUploadingReceipt(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Sila isi nama penuh";
    if (!form.phone.trim()) errs.phone = "Sila isi no. telefon";
    if (!form.ic_number.replace(/-/g, "").trim()) errs.ic_number = "Sila isi no. kad pengenalan (IC)";
    else if (!/^\d{12}$/.test(form.ic_number.replace(/-/g, ""))) errs.ic_number = "No. IC tidak sah (12 digit)";
    if (!form.gender) errs.gender = "Sila pilih jantina";
    if (!form.location.trim()) errs.location = "Sila isi lokasi/daerah";
    if (!form.current_occupation.trim()) errs.current_occupation = "Sila isi pekerjaan sekarang";
    if (!form.prior_training) errs.prior_training = "Sila pilih jawapan";
    if (!form.next_of_kin_name.trim()) errs.next_of_kin_name = "Sila isi nama waris";
    if (!form.next_of_kin_phone.trim()) errs.next_of_kin_phone = "Sila isi no. telefon waris";
    if (!form.payment_method) errs.payment_method = "Sila pilih kaedah bayaran";
    if (form.payment_method === "billplz" && !form.email.trim()) errs.email = "Emel diperlukan untuk pembayaran Billplz";
    if (!agreed) errs.agreed = "Sila tandakan pengesahan minat";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/course-leads", { ...form });
      if (form.payment_method === "billplz") {
        if (!res.data.billplz_bill_url) {
          toast.error("Gagal cipta bil Billplz. Sila cuba lagi atau pilih Bank Transfer.");
          setSubmitting(false);
          return;
        }
        toast.success("Mengalihkan ke Billplz untuk pembayaran...");
        window.location.href = res.data.billplz_bill_url;
        return;
      }
      toast.success("Pendaftaran anda berjaya direkodkan!");
      setSubmitted(true);
      setForm(EMPTY);
      setAgreed(false);
      setReceiptFileName("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghantar. Sila cuba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="daftar" data-testid="kursus-registration" className="relative bg-kursus-bg py-20 sm:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(195,21,30,0.15),transparent_60%)]" />
      <div className="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="font-anton uppercase text-3xl sm:text-4xl text-kursus-white mb-3">
            DAFTAR MINAT <span className="text-kursus-gold">SEKARANG</span>
          </h2>
          <p className="text-kursus-muted text-sm sm:text-base">
            Isi maklumat ringkas. Pendaftaran anda akan direkodkan terus dalam sistem PPPS.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="kursus-form-success"
            className="bg-kursus-card border border-kursus-gold/40 rounded-xl p-8 text-center"
          >
            <CheckCircle2 className="w-12 h-12 text-kursus-gold mx-auto mb-4" />
            <h3 className="font-anton uppercase text-xl text-kursus-white mb-2">PENDAFTARAN BERJAYA</h3>
            <p className="text-kursus-muted text-sm mb-6">
              Terima kasih. Maklumat anda telah direkodkan dalam sistem PPPS. {CONTACT_NAME} akan menghubungi anda untuk pengesahan seterusnya.
            </p>
            <Button
              type="button"
              onClick={() => setSubmitted(false)}
              data-testid="kursus-form-register-another-btn"
              variant="outline"
              className="border-kursus-gold/50 text-kursus-white hover:bg-kursus-gold hover:text-black"
            >
              Daftar Peserta Lain
            </Button>
          </motion.div>
        ) : (
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={submit}
          className="bg-kursus-card border border-kursus-border rounded-xl p-6 sm:p-8 space-y-5"
        >
          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Nama Penuh</Label>
            <Input data-testid="kursus-form-name" value={form.full_name} onChange={set("full_name")} className={`mt-1.5 ${darkField}`} />
            {errors.full_name && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-name-error">{errors.full_name}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">No. Telefon / WhatsApp</Label>
            <Input data-testid="kursus-form-phone" value={form.phone} onChange={set("phone")} placeholder="01X-XXX XXXX" className={`mt-1.5 ${darkField}`} />
            {errors.phone && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-phone-error">{errors.phone}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">No. Kad Pengenalan (IC)</Label>
            <Input data-testid="kursus-form-ic" value={form.ic_number} onChange={set("ic_number")} placeholder="900101-13-1234" className={`mt-1.5 ${darkField}`} />
            {errors.ic_number && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-ic-error">{errors.ic_number}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Jantina</Label>
            <div className="flex gap-3 mt-1.5">
              {["Lelaki", "Perempuan"].map((g) => (
                <button
                  type="button"
                  key={g}
                  data-testid={`kursus-form-gender-${g.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, gender: g }))}
                  className={`flex-1 py-2.5 rounded-md border font-oswald text-sm transition-colors ${
                    form.gender === g ? "bg-kursus-gold text-black border-kursus-gold" : "bg-kursus-bg2 text-kursus-white/80 border-kursus-border hover:border-kursus-gold/60"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            {errors.gender && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-gender-error">{errors.gender}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Lokasi / Daerah</Label>
            <Input data-testid="kursus-form-location" value={form.location} onChange={set("location")} className={`mt-1.5 ${darkField}`} />
            {errors.location && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-location-error">{errors.location}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Pekerjaan Sekarang</Label>
            <Input data-testid="kursus-form-occupation" value={form.current_occupation} onChange={set("current_occupation")} className={`mt-1.5 ${darkField}`} />
            {errors.current_occupation && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-occupation-error">{errors.current_occupation}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Pernah Mengikuti Kursus Keselamatan?</Label>
            <div className="flex gap-3 mt-1.5">
              {["Ya", "Tidak"].map((g) => (
                <button
                  type="button"
                  key={g}
                  data-testid={`kursus-form-prior-${g.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, prior_training: g }))}
                  className={`flex-1 py-2.5 rounded-md border font-oswald text-sm transition-colors ${
                    form.prior_training === g ? "bg-kursus-gold text-black border-kursus-gold" : "bg-kursus-bg2 text-kursus-white/80 border-kursus-border hover:border-kursus-gold/60"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            {errors.prior_training && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-prior-error">{errors.prior_training}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Nama Waris</Label>
            <Input data-testid="kursus-form-kin-name" value={form.next_of_kin_name} onChange={set("next_of_kin_name")} className={`mt-1.5 ${darkField}`} />
            {errors.next_of_kin_name && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-kin-name-error">{errors.next_of_kin_name}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">No. Telefon Waris</Label>
            <Input data-testid="kursus-form-kin-phone" value={form.next_of_kin_phone} onChange={set("next_of_kin_phone")} placeholder="01X-XXX XXXX" className={`mt-1.5 ${darkField}`} />
            {errors.next_of_kin_phone && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-kin-phone-error">{errors.next_of_kin_phone}</p>}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Kaedah Bayaran</Label>
            <div className="flex gap-3 mt-1.5">
              <button
                type="button"
                data-testid="kursus-form-method-bank_transfer"
                onClick={() => setForm((f) => ({ ...f, payment_method: "bank_transfer" }))}
                className={`flex-1 py-2.5 rounded-md border font-oswald text-xs sm:text-sm transition-colors flex items-center justify-center gap-1.5 ${
                  form.payment_method === "bank_transfer" ? "bg-kursus-gold text-black border-kursus-gold" : "bg-kursus-bg2 text-kursus-white/80 border-kursus-border hover:border-kursus-gold/60"
                }`}
              >
                <Landmark className="w-3.5 h-3.5" /> Bank Transfer
              </button>
              <button
                type="button"
                data-testid="kursus-form-method-billplz"
                disabled={!paymentInfo?.billplz_available}
                onClick={() => setForm((f) => ({ ...f, payment_method: "billplz" }))}
                className={`flex-1 py-2.5 rounded-md border font-oswald text-xs sm:text-sm transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${
                  form.payment_method === "billplz" ? "bg-kursus-gold text-black border-kursus-gold" : "bg-kursus-bg2 text-kursus-white/80 border-kursus-border hover:border-kursus-gold/60"
                }`}
              >
                <CreditCard className="w-3.5 h-3.5" /> Billplz {!paymentInfo?.billplz_available && "(Belum Tersedia)"}
              </button>
            </div>
            {errors.payment_method && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-method-error">{errors.payment_method}</p>}

            {form.payment_method === "bank_transfer" && (
              <div className="mt-3 p-4 rounded-md bg-kursus-bg2 border border-kursus-border" data-testid="kursus-form-bank-details">
                {paymentInfo?.bank_name ? (
                  <>
                    <p className="text-kursus-gold text-xs font-oswald font-semibold mb-1.5">BUTIRAN AKAUN BANK</p>
                    <p className="text-kursus-white text-sm">{paymentInfo.bank_name} — {paymentInfo.bank_account_number}</p>
                    <p className="text-kursus-muted text-xs mt-0.5">{paymentInfo.bank_account_holder}</p>
                  </>
                ) : (
                  <p className="text-kursus-muted text-xs">Butiran bank akan diberikan oleh {CONTACT_NAME} melalui WhatsApp selepas anda hantar borang ini.</p>
                )}

                <div className="mt-3.5 pt-3.5 border-t border-kursus-border">
                  <p className="text-kursus-white/90 font-oswald text-xs font-semibold mb-1.5">MUAT NAIK RESIT PEMBAYARAN (PILIHAN)</p>
                  <input ref={receiptInputRef} type="file" accept="image/*,.pdf" data-testid="kursus-form-receipt-input" className="hidden" onChange={(e) => uploadReceipt(e.target.files?.[0])} />
                  {form.payment_receipt_file_id ? (
                    <div className="flex items-center justify-between gap-2 py-2 px-3 rounded-md bg-kursus-bg border border-kursus-gold/40" data-testid="kursus-form-receipt-uploaded">
                      <span className="flex items-center gap-2 text-kursus-white text-xs truncate">
                        <FileCheck2 className="w-4 h-4 text-kursus-gold shrink-0" /> <span className="truncate">{receiptFileName || "Resit dimuat naik"}</span>
                      </span>
                      <button type="button" data-testid="kursus-form-receipt-remove-btn" onClick={() => { setForm((f) => ({ ...f, payment_receipt_file_id: "" })); setReceiptFileName(""); }} className="text-kursus-muted hover:text-kursus-red shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid="kursus-form-receipt-upload-btn"
                      disabled={uploadingReceipt}
                      onClick={() => receiptInputRef.current?.click()}
                      className="w-full py-2.5 rounded-md border-2 border-dashed border-kursus-border flex items-center justify-center gap-2 text-kursus-muted hover:border-kursus-gold/60 hover:text-kursus-white transition-colors text-xs font-oswald disabled:opacity-60"
                    >
                      {uploadingReceipt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploadingReceipt ? "Memuat naik..." : "Klik untuk muat naik resit"}
                    </button>
                  )}
                  <p className="text-kursus-muted text-[11px] mt-2">Sudah transfer? Muat naik resit di atas. Belum transfer? Boleh hantar resit kemudian melalui WhatsApp.</p>
                </div>
              </div>
            )}

            {form.payment_method === "billplz" && (
              <div className="mt-3">
                <Label className="text-kursus-white/90 font-oswald text-sm">Emel (untuk resit pembayaran)</Label>
                <Input data-testid="kursus-form-email" type="email" value={form.email} onChange={set("email")} className={`mt-1.5 ${darkField}`} />
                {errors.email && <p className="text-kursus-red text-xs mt-1" data-testid="kursus-form-email-error">{errors.email}</p>}
              </div>
            )}
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Jumlah Bayaran</Label>
            <div className="mt-1.5 flex items-center justify-between py-2.5 px-4 rounded-md border border-kursus-gold bg-kursus-gold/10">
              <span className="font-oswald text-sm text-kursus-white">Deposit Pendaftaran</span>
              <span className="font-anton text-lg text-kursus-gold">{COURSE_DEPOSIT}</span>
            </div>
            <p className="text-kursus-muted text-[11px] mt-1.5">Baki yuran dibayar semasa lapor diri di kem.</p>
          </div>

          <div>
            <Label className="text-kursus-white/90 font-oswald text-sm">Catatan (pilihan)</Label>
            <Textarea data-testid="kursus-form-notes" value={form.notes} onChange={set("notes")} className={`mt-1.5 ${darkField}`} rows={3} />
          </div>

          <div className="flex items-start gap-3 pt-1">
            <Checkbox id="agree" data-testid="kursus-form-agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 border-kursus-gold/60 data-[state=checked]:bg-kursus-gold data-[state=checked]:text-black" />
            <Label htmlFor="agree" className="text-xs sm:text-sm text-kursus-white/80 leading-relaxed">
              Saya berminat untuk menyertai Kursus Asas Pengawal Peribadi.
            </Label>
          </div>
          {errors.agreed && <p className="text-kursus-red text-xs" data-testid="kursus-form-agree-error">{errors.agreed}</p>}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="kursus-form-submit"
            className="w-full bg-kursus-red hover:bg-kursus-reddark text-white font-oswald font-bold tracking-wide py-6 rounded-md gap-2 border border-kursus-gold/50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {form.payment_method === "billplz" ? "TERUSKAN KE BILLPLZ" : "HANTAR PENDAFTARAN"}
          </Button>
        </motion.form>
        )}
      </div>
    </section>
  );
}
